import React, { useEffect, useState, useReducer } from 'react'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import cx from 'classnames'
import dayjs from 'dayjs'
import build from 'redux-object'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'

import Alert from '../Alert'
import Box from '../Box'
import Breadcrumb from '../Breadcrumb'
import ConditionalWrapper from '../ConditionalWrapper'
import Dropdown from '../Dropdown'
import InputDatePicker from '../InputDatePicker'
import LatestMeasurements from '../LatestMeasurements'
import NotFound from '../NotFound'
import { PageHeading, FallbackText } from '../TextElements'
import UsageSummary from '../UsageSummary'
import LineChartsByRoom from '../LineChartsByRoom'

import coldWaterIcon from '../graphics/cold-water.svg'
import electricityIcon from '../graphics/electricity.svg'
import heatIcon from '../graphics/heat.svg'
import hotWaterIcon from '../graphics/hot-water.svg'
import radiatorIcon from '../graphics/radiator.svg'
import ventilatorIcon from '../graphics/ventilator.svg'
import { formatDate, parseDate, isValidDateRange } from '../helpers/dates'
import {
  loadHousehold,
  loadDevicesByHousehold,
  loadTenancies,
} from '../helpers/api'

import { fetchVentilationReport, fetchCo2Report } from '../helpers/api'

const DATE_FORMAT = 'YYYY-MM-DD'

function usagePeriodReducer(state, action) {
  switch (action.type) {
    case 'SET_FROM_DATE':
      return {
        ...state,
        fromDate: action.value,
        isDateRangeValid: isValidDateRange(action.value, state.toDate),
      }
    case 'SET_TO_DATE':
      return {
        ...state,
        toDate: action.value,
        isDateRangeValid: isValidDateRange(state.fromDate, action.value),
      }

    case 'SET_DATES':
      const [fromDate, toDate] = (action.value || '').split('|')
      return {
        ...state,
        fromDate,
        toDate,
        isDateRangeValid: isValidDateRange(fromDate, toDate),
      }
    default:
      return state
  }
}

/* Main component ----------------------------------------------------------- */
export default function Household({ householdId, navigate }) {
  const [loading, setLoading] = useState(true)
  const [resources, setResources] = useState({})
  const [errorMessage, setErrorMessage] = useState(null)
  const [usagePeriod, usagePeriodDispatch] = useReducer(usagePeriodReducer)

  // Define upfront in order to use as input for useEffect — default to empty
  // object to access `household.apartment` before data is loaded
  const household = build(resources, 'households', householdId) || {}

  // Load data
  useEffect(() => {
    async function _loadData() {
      try {
        await loadHousehold(
          {
            householdId,
          },
          setResources
        )
        await loadTenancies(null, setResources)
        await loadDevicesByHousehold(
          {
            householdId,
          },
          setResources
        )
        setLoading(false)
      } catch (error) {
        setErrorMessage(error.message)
        setLoading(false)
      }
    }
    _loadData()
  }, [householdId])

  // Set document title
  useEffect(() => {
    document.title = loading
      ? 'Henter… | Wise Home'
      : `${household.apartment || household.number || household.id} | Wise Home`
    // Reset title when component unmounts
    return () => {
      document.title = 'Wise Home'
    }
  }, [household.apartment, household.number, household.id, loading])

  const devices = build(resources, 'devices') || []

  const heat_cost_devices = R.filter(
    R.pathEq(['deviceType', 'kind'], 'heat_cost')
  )(devices)

  const heat_devices = R.filter(R.pathEq(['deviceType', 'kind'], 'heat'))(
    devices
  )

  const hot_water_devices = R.filter(
    R.pathEq(['deviceType', 'kind'], 'hot_water')
  )(devices)

  const cold_water_devices = R.filter(
    R.pathEq(['deviceType', 'kind'], 'cold_water')
  )(devices)

  const electricity_devices = R.filter(
    R.pathSatisfies(R.either(R.equals('pulse'), R.equals('electricity')), [
      'deviceType',
      'kind',
    ])
  )(devices)

  const humidity_devices = R.filter(
    R.pathSatisfies(R.equals('humidity'), ['deviceType', 'kind'])
  )(devices)

  const co2_devices = R.filter(
    R.pathSatisfies(R.equals('co2'), ['deviceType', 'kind'])
  )(devices)

  const tenancies = build(resources, 'tenancies') || []
  const { moveInDate, moveOutDate } = R.pipe(
    R.find(R.pathEq(['household', 'id'], householdId)),
    R.defaultTo({})
  )(tenancies)
  // If the tenancy ended in the past, set that as default usage reference date
  const usageEndDate =
    moveOutDate && dayjs(moveOutDate).isBefore(dayjs(), 'day')
      ? moveOutDate
      : dayjs().format('YYYY-MM-DD')

  // Set usage period
  useEffect(() => {
    if (!loading) {
      usagePeriodDispatch({
        type: 'SET_DATES',
        value: `${dayjs(usageEndDate)
          .subtract(6, 'days')
          .format(DATE_FORMAT)}|${dayjs(usageEndDate).format(DATE_FORMAT)}`,
      })
    }
  }, [loading, usageEndDate])

  const isVirtual = get(household, 'virtual', false)

  const getDateFromEvent = function (event) {
    const dateString = event.target.value
    const date = parseDate(dateString)
    return date && dayjs(date).isValid() ? date : false
  }

  if (!loading && isEmpty(household)) {
    return (
      <NotFound>{`Husstand med id ${householdId} kan ikke findes.`}</NotFound>
    )
  }

  return (
    <div>
      <Breadcrumb
        links={[
          {
            url: `../../`,
            title: 'Perioder',
          },
          {
            title: loading
              ? 'Henter…'
              : `${formatDate(moveInDate, 'DD.MM.YYYY', '?')} — ${formatDate(
                  moveOutDate,
                  'DD.MM.YYYY',
                  '?'
                )}`,
          },
        ]}
      />
      <div className="flex items-center">
        <PageHeading>
          {loading
            ? 'Henter…'
            : `${get(
                household,
                'address.streetAndNumber',
                'Ukendt adresse'
              )}, ${household.apartment || householdId}`}
        </PageHeading>
      </div>
      {errorMessage && (
        <Alert type="error" className="mt3">
          {errorMessage}
        </Alert>
      )}
      <div>
        {usagePeriod && (
          <div>
            <div className="mb3 pv2 f6 flex flex-column flex-row-l">
              <div className="flex flex-row flex-grow-1 justify-between items-center">
                <span className="moon-gray mr-auto">Vist periode:</span>
                <Dropdown
                  id="period-selector"
                  items={[
                    { text: 'Selvvalgt periode', value: null },
                    {
                      text: 'Seneste 7 dage',
                      value:
                        dayjs(usageEndDate)
                          .subtract(6, 'days')
                          .format(DATE_FORMAT) +
                        '|' +
                        dayjs(usageEndDate).format(DATE_FORMAT),
                    },
                    {
                      text: 'Seneste 21 dage',
                      value:
                        dayjs(usageEndDate)
                          .subtract(20, 'days')
                          .format(DATE_FORMAT) +
                        '|' +
                        dayjs(usageEndDate).format(DATE_FORMAT),
                    },
                    {
                      text: 'Seneste 12 uger',
                      value:
                        dayjs(usageEndDate)
                          .subtract(12, 'weeks')
                          .startOf('week')
                          .format(DATE_FORMAT) +
                        '|' +
                        dayjs(usageEndDate)
                          .subtract(1, 'week')
                          .endOf('week')
                          .format(DATE_FORMAT),
                    },
                    {
                      text: 'Indeværende år',
                      value:
                        dayjs(usageEndDate)
                          .startOf('year')
                          .format(DATE_FORMAT) +
                        '|' +
                        dayjs(usageEndDate).format(DATE_FORMAT),
                    },
                  ]}
                  className="flex-grow-1 w5-l ml2 mr3-l"
                  value={usagePeriod.fromDate + '|' + usagePeriod.toDate}
                  onChange={(value) => {
                    if (value) {
                      usagePeriodDispatch({ type: 'SET_DATES', value })
                    }
                  }}
                />
              </div>
              <div className="flex flex-row items-center mt2 mt0-l">
                <div className="flex-grow-1 w5-l">
                  <InputDatePicker
                    id="period-from"
                    value={new Date(usagePeriod.fromDate)}
                    errorMessage={
                      usagePeriod.isDateRangeValid
                        ? undefined
                        : 'Indtast ældre dato'
                    }
                    onChange={(date) => {
                      const isDateRangeValid = isValidDateRange(
                        date,
                        usagePeriod.toDate
                      )
                      if (date && dayjs(date).isValid() && isDateRangeValid) {
                        usagePeriodDispatch({
                          type: 'SET_FROM_DATE',
                          value: dayjs(date).format(DATE_FORMAT),
                        })
                      }
                    }}
                    onBlur={(event) => {
                      const date = getDateFromEvent(event)
                      if (date) {
                        usagePeriodDispatch({
                          type: 'SET_FROM_DATE',
                          value: dayjs(date).format(DATE_FORMAT),
                        })
                      }
                    }}
                    onKeyPress={(event) => {
                      if (event.key === 'Enter') {
                        const date = getDateFromEvent(event)
                        if (date) {
                          usagePeriodDispatch({
                            type: 'SET_FROM_DATE',
                            value: dayjs(date).format(DATE_FORMAT),
                          })
                        }
                      }
                    }}
                    toMonth={new Date(usagePeriod.toDate)}
                    disabledDays={(date) =>
                      dayjs(date).isAfter(
                        dayjs(usagePeriod.toDate).subtract(6, 'days'),
                        'day'
                      )
                    }
                  />
                </div>
                <span className="mh2 moon-gray">—</span>
                <div className="flex-grow-1 w5-l">
                  <InputDatePicker
                    id="period-to"
                    value={new Date(usagePeriod.toDate)}
                    errorMessage={
                      usagePeriod.isDateRangeValid
                        ? undefined
                        : 'Indtast nyere dato'
                    }
                    onChange={(date) => {
                      const isDateRangeValid = isValidDateRange(
                        usagePeriod.fromDate,
                        date
                      )
                      if (date && dayjs(date).isValid() && isDateRangeValid) {
                        usagePeriodDispatch({
                          type: 'SET_TO_DATE',
                          value: dayjs(date).format(DATE_FORMAT),
                        })
                      }
                    }}
                    onBlur={(event) => {
                      const date = getDateFromEvent(event)
                      if (date) {
                        usagePeriodDispatch({
                          type: 'SET_TO_DATE',
                          value: dayjs(date).format(DATE_FORMAT),
                        })
                      }
                    }}
                    onKeyPress={(event) => {
                      if (event.key === 'Enter') {
                        const date = getDateFromEvent(event)
                        if (date) {
                          usagePeriodDispatch({
                            type: 'SET_TO_DATE',
                            value: dayjs(date).format(DATE_FORMAT),
                          })
                        }
                      }
                    }}
                    fromMonth={new Date(usagePeriod.fromDate)}
                    toMonth={new Date()}
                    disabledDays={(date) =>
                      dayjs(date).isBefore(
                        dayjs(usagePeriod.fromDate).add(6, 'days'),
                        'day'
                      ) || dayjs(date).isAfter(dayjs(usageEndDate), 'day')
                    }
                  />
                </div>
              </div>
            </div>
            {devices.length === 0 ? (
              !loading && (
                <div className="mv5 tc">
                  <FallbackText>Ingen enheder installeret</FallbackText>
                </div>
              )
            ) : (
              <div
                id="usage-summaries"
                className="flex flex-row flex-wrap nl2 nl3-l nr2 nr3-l"
              >
                {heat_cost_devices.length > 0 ? (
                  <UsageSummary
                    title="Radiatorer"
                    icon={radiatorIcon}
                    type="heat-cost-reports"
                    householdId={householdId}
                    fromDate={usagePeriod.fromDate}
                    toDate={usagePeriod.toDate}
                  >
                    <LatestMeasurements devices={heat_cost_devices} />
                  </UsageSummary>
                ) : null}
                {heat_devices.length > 0 ? (
                  <UsageSummary
                    title="Varme"
                    icon={heatIcon}
                    type="heat-usage-reports"
                    householdId={householdId}
                    fromDate={usagePeriod.fromDate}
                    toDate={usagePeriod.toDate}
                  >
                    <LatestMeasurements devices={heat_devices} />
                  </UsageSummary>
                ) : null}
                {cold_water_devices.length > 0 ? (
                  <UsageSummary
                    title="Koldt vand"
                    icon={coldWaterIcon}
                    type="water-usage-reports"
                    waterType="cold"
                    householdId={householdId}
                    fromDate={usagePeriod.fromDate}
                    toDate={usagePeriod.toDate}
                  >
                    <LatestMeasurements devices={cold_water_devices} />
                  </UsageSummary>
                ) : null}
                {hot_water_devices.length > 0 ? (
                  <UsageSummary
                    title="Varmt vand"
                    icon={hotWaterIcon}
                    type="water-usage-reports"
                    waterType="hot"
                    householdId={householdId}
                    fromDate={usagePeriod.fromDate}
                    toDate={usagePeriod.toDate}
                  >
                    <LatestMeasurements devices={hot_water_devices} />
                  </UsageSummary>
                ) : null}
                {electricity_devices.length > 0 ? (
                  <UsageSummary
                    title="Elektricitet"
                    icon={electricityIcon}
                    className="flex-grow-1"
                    type="electricity-usage-reports"
                    householdId={householdId}
                    fromDate={usagePeriod.fromDate}
                    toDate={usagePeriod.toDate}
                  >
                    <LatestMeasurements devices={electricity_devices} />
                  </UsageSummary>
                ) : null}

                <ConditionalWrapper
                  condition={
                    humidity_devices.length > 0 && co2_devices.length > 0
                  }
                  wrapper={(children) => (
                    <div className="flex-l flex-m flex-row-l flex-row-m">
                      {children}
                    </div>
                  )}
                >
                  {humidity_devices.length > 0 ? (
                    <LineChartsByRoom
                      id="humidity-charts-by-room"
                      title="Luftfugtighed"
                      description="Grafen viser forskellen i den absolute fugt inde og ude. Dette er
                    med andre ord den fugt der produceres indendørs, og som ikke luftes
                    ud. Ved god ventilation bør denne værdi ligge under 4 g/m³."
                      icon={ventilatorIcon}
                      fromDate={usagePeriod.fromDate}
                      toDate={usagePeriod.toDate}
                      fetchReportFunction={fetchVentilationReport}
                      rooms={R.pipe(
                        R.pluck('room'),
                        R.map(R.pick(['id', 'name'])),
                        R.sortBy(R.prop('name'))
                      )(humidity_devices)}
                    >
                      <LatestMeasurements devices={humidity_devices} />
                    </LineChartsByRoom>
                  ) : null}
                  {co2_devices.length > 0 ? (
                    <LineChartsByRoom
                      icon={ventilatorIcon}
                      id="co2-charts-by-room"
                      title={['CO', <sub>2</sub>]}
                      description={[
                        'Grafen viser CO',
                        <sub>2</sub>,
                        ' indholdet i luften. Ved god ventilation bør denne værdi ligge under 800 ppm. Hvis CO',
                        <sub>2</sub>,
                        ' værdien er for høj, anbefales det at øge ventilationen i rummet, eksempelvis kan det være at åbne et vindue',
                      ]}
                      fromDate={usagePeriod.fromDate}
                      toDate={usagePeriod.toDate}
                      fetchReportFunction={fetchCo2Report}
                      rooms={R.pipe(
                        R.pluck('room'),
                        R.map(R.pick(['id', 'name'])),
                        R.sortBy(R.prop('name'))
                      )(co2_devices)}
                    >
                      <LatestMeasurements devices={co2_devices} />
                    </LineChartsByRoom>
                  ) : null}
                </ConditionalWrapper>
              </div>
            )}
          </div>
        )}
        <div className="no-break">
          <h3 className="mb3 fw9 f4">Oplysninger</h3>
          {isVirtual ? (
            <FallbackText>Fællesareal: Ingen beboere</FallbackText>
          ) : (
            <div className="w-100 w-50-l pr3-l">
              <Box>
                <table className="f6 w-100 lh-copy collapse">
                  <tbody>
                    <tr>
                      <Th className="pt2 pb1">Indflytning</Th>
                      <Td className="pt2 pb1">{formatDate(moveInDate)}</Td>
                    </tr>
                    <tr>
                      <Th className="pt1 pb2">Udflytning</Th>
                      <Td className="pt1 pb2">{formatDate(moveOutDate)}</Td>
                    </tr>
                  </tbody>
                </table>
              </Box>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

Household.propTypes = {
  householdId: PropTypes.string,
  navigate: PropTypes.func,
}

/* Sub components ----------------------------------------------------------- */
function Th({ valign = 'top', className, children }) {
  return (
    <th
      className={cx(
        'tl pr3 moon-gray b--light-silver normal',
        `v-${valign}`,
        className
      )}
      scope="row"
    >
      {children}
    </th>
  )
}

function Td({ className, children }) {
  return <td className={cx('navy b--light-silver', className)}>{children}</td>
}
